import { useEffect, useState } from "react";

import { CouponDto, CouponTypeDto } from "services/coupons/restModels";
import { CouponRequestDto } from "services/coupons/restRequests";
import { CurrencyFormatter } from "shared/lib/currencyFormatter";
import { parseDecimalInput, sanitizeDecimalInput } from "shared/lib/decimalInput";
import {
    dateToInputValue,
    endOfDay,
    inputValueToDate,
    startOfDay,
} from "shared/lib/dateInput";
import { digitsOnly } from "shared/lib/formInput";

import {
    DEFAULT_COUPON_PERIOD_MONTHS,
    DEFAULT_DISCOUNT_PERCENT,
    DEFAULT_TOTAL_USAGES_LIMIT,
} from "features/Coupons/constants";

export interface CouponFormState {
    code: string;
    setCode: (value: string) => void;
    generateCode: () => void;

    type: CouponTypeDto;
    setType: (value: CouponTypeDto) => void;
    // Тип задаётся один раз при создании: сменить его у существующего промокода нельзя.
    isTypeLocked: boolean;

    // Скидка вводится строкой: у процентов это целое, у суммы — дробное в основных
    // единицах, и промежуточные состояния ввода («10,») числом не выражаются.
    discount: string;
    setDiscount: (value: string) => void;

    totalUsagesLimit: string;
    setTotalUsagesLimit: (value: string) => void;
    isUnlimited: boolean;

    startDate: string;
    setStartDate: (value: string) => void;
    endDate: string;
    setEndDate: (value: string) => void;

    error: string | null;
    canSubmit: boolean;
    // null — форма не прошла проверку; текст ошибки лежит в error.
    buildValues: () => CouponRequestDto | null;
}

const CODE_MAX_LENGTH = 32;
const USAGES_MAX_DIGITS = 6;
const PERCENT_MAX_DIGITS = 2;
const MIN_DISCOUNT_PERCENT = 1;
const MAX_DISCOUNT_PERCENT = 99;

const defaultStartDate = () => new Date();

const defaultEndDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + DEFAULT_COUPON_PERIOD_MONTHS);
    return date;
};

const generateCouponCode = (): string =>
    String(Math.floor(Math.random() * (999999 - 100000) + 100000));

/*
* Локальный стейт формы промокода. Отдельным хуком, потому что диалог заполняется двумя
* способами — пустой формой на создание и полями существующего промокода на правку.
* currencyFormatter — валюты редактируемого промокода: в ней и показываем сумму, и
* переводим введённое обратно в минорные единицы.
* */
export const useCouponForm = (
    coupon: CouponDto | null,
    currencyFormatter: CurrencyFormatter
): CouponFormState => {
    const [code, setCodeState] = useState<string>("");
    const [type, setTypeState] = useState<CouponTypeDto>("Percentage");
    const [discount, setDiscountState] = useState<string>(String(DEFAULT_DISCOUNT_PERCENT));
    const [totalUsagesLimit, setTotalUsagesLimitState] = useState<string>(
        String(DEFAULT_TOTAL_USAGES_LIMIT)
    );
    const [startDate, setStartDate] = useState<string>(dateToInputValue(defaultStartDate()));
    const [endDate, setEndDate] = useState<string>(dateToInputValue(defaultEndDate()));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setError(null);

        if (!coupon) {
            setCodeState("");
            setTypeState("Percentage");
            setDiscountState(String(DEFAULT_DISCOUNT_PERCENT));
            setTotalUsagesLimitState(String(DEFAULT_TOTAL_USAGES_LIMIT));
            setStartDate(dateToInputValue(defaultStartDate()));
            setEndDate(dateToInputValue(defaultEndDate()));
            return;
        }

        setCodeState(coupon.code);
        setTypeState(coupon.type);
        setDiscountState(
            coupon.type === "Percentage"
                ? String(coupon.discountPercent)
                : currencyFormatter.toShow(coupon.discountAmount, true)
        );
        setTotalUsagesLimitState(String(coupon.totalUsagesLimit));
        setStartDate(dateToInputValue(new Date(coupon.startDate)));
        setEndDate(dateToInputValue(new Date(coupon.endDate)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coupon]);

    // Проценты — целое, сумма — дробное: у поля один смысл, но разный допустимый ввод.
    const setDiscount = (value: string) =>
        setDiscountState(
            type === "Percentage"
                ? digitsOnly(value, PERCENT_MAX_DIGITS)
                : sanitizeDecimalInput(value)
        );

    // Код хранится в верхнем регистре: по нему ищет гостевое меню.
    const setCode = (value: string) =>
        setCodeState(value.toUpperCase().replace(/\s/g, "").slice(0, CODE_MAX_LENGTH));

    const generateCode = () => setCodeState(generateCouponCode());

    const setType = (value: CouponTypeDto) => {
        setTypeState(value);
        // Величина скидки у процентов и у суммы несопоставима: 10 % при переключении
        // превратились бы в 10 ₽.
        setDiscountState(value === "Percentage" ? String(DEFAULT_DISCOUNT_PERCENT) : "");
    };

    const setTotalUsagesLimit = (value: string) =>
        setTotalUsagesLimitState(digitsOnly(value, USAGES_MAX_DIGITS));

    const isUnlimited = totalUsagesLimit.trim() !== "" && Number(totalUsagesLimit) === 0;

    const canSubmit =
        code.trim() !== "" &&
        discount.trim() !== "" &&
        totalUsagesLimit.trim() !== "" &&
        startDate !== "" &&
        endDate !== "";

    const buildValues = (): CouponRequestDto | null => {
        const start = inputValueToDate(startDate);
        const end = inputValueToDate(endDate);

        if (!start || !end) {
            setError("Укажите срок действия промокода");
            return null;
        }

        if (endOfDay(end).getTime() <= startOfDay(start).getTime()) {
            setError("Дата окончания должна быть позже даты начала");
            return null;
        }

        const usagesLimit = Number(totalUsagesLimit);

        if (isNaN(usagesLimit)) {
            setError("Укажите количество использований");
            return null;
        }

        // Начало действия — с начала выбранного дня, конец — по его конец включительно,
        // иначе последний день срока оказывается нерабочим.
        const period = {
            code: code.trim(),
            totalUsagesLimit: usagesLimit,
            startDate: startOfDay(start).getTime(),
            endDate: endOfDay(end).getTime(),
        };

        if (type === "Percentage") {
            const percent = Number(discount);

            if (isNaN(percent) || percent < MIN_DISCOUNT_PERCENT || percent > MAX_DISCOUNT_PERCENT) {
                setError(
                    `Скидка в процентах должна быть от ${MIN_DISCOUNT_PERCENT} до ${MAX_DISCOUNT_PERCENT}`
                );
                return null;
            }

            setError(null);

            return { ...period, type, discountPercent: percent };
        }

        const amount = parseDecimalInput(discount);

        if (amount === null) {
            setError("Укажите сумму скидки");
            return null;
        }

        const minorUnits = currencyFormatter.toSend(amount);

        if (minorUnits <= 0) {
            setError("Укажите сумму скидки");
            return null;
        }

        setError(null);

        return { ...period, type, discountAmount: minorUnits };
    };

    return {
        code,
        setCode,
        generateCode,
        type,
        setType,
        isTypeLocked: coupon !== null,
        discount,
        setDiscount,
        totalUsagesLimit,
        setTotalUsagesLimit,
        isUnlimited,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        error,
        canSubmit,
        buildValues,
    };
};
